<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { ColoredButton, FormInput, IconImage, ThemeProvider, UiModalHeader } from '@/shared/ui';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';
  import PlusButtonIcon from '@/shared/assets/icons/PlusButtonIcon.svg';
  import MinusButtonIcon from '@/shared/assets/icons/MinusButtonIcon.svg';
  import { findDuplicateFieldIndices } from '../lib/settings-duplicate';

  const props = withDefaults(
    defineProps<{
      mode: 'add' | 'edit';
      title: string;
      fieldLabel: string;
      fieldPlaceholder: string;
      initialValue?: string;
      maxFields?: number;
      isSaving?: boolean;
      duplicateIndices?: readonly number[];
      validationMessages?: readonly string[];
    }>(),
    {
      initialValue: '',
      maxFields: 5,
      isSaving: false,
      duplicateIndices: () => [],
      validationMessages: () => [],
    }
  );

  const emit = defineEmits<{
    close: [];
    save: [values: string[]];
    duplicate: [];
  }>();

  const NAME_MAX_LENGTH = 100;

  let nextFieldId = 2;
  const fields = ref<{ id: number; value: string }[]>([{ id: 1, value: props.initialValue }]);
  const localDuplicateIndices = ref<Set<number>>(new Set());
  const dismissedIndices = ref<Set<number>>(new Set());
  const isValidationDismissed = ref(false);

  /**
   * The local check flags only a name this request repeats; the API flags what only it knows, by
   * position of the names as sent. Editing a field, or removing one (which shifts every position),
   * drops the highlight until the next save attempt.
   */
  const duplicateFieldIndices = computed(() => {
    const conflicts = new Set([...localDuplicateIndices.value, ...props.duplicateIndices]);
    dismissedIndices.value.forEach((index) => conflicts.delete(index));
    return conflicts;
  });

  const canAddField = computed(() => props.mode === 'add' && fields.value.length < props.maxFields);
  const isFormValid = computed(() => fields.value.some((field) => field.value.trim().length > 0));

  /**
   * The API reports a refused payload per field and not per position, so a single field takes the
   * message on itself and a batch shows it once below: picking one of several would be a guess.
   */
  const visibleValidationMessages = computed(() =>
    isValidationDismissed.value ? [] : props.validationMessages
  );

  const singleFieldValidationMessage = computed(() =>
    fields.value.length === 1 ? visibleValidationMessages.value[0] : undefined
  );

  const batchValidationMessages = computed(() =>
    fields.value.length > 1 ? visibleValidationMessages.value : []
  );

  const fieldError = (index: number): string | undefined => {
    if (duplicateFieldIndices.value.has(index)) return 'Nome duplicado';

    return index === 0 ? singleFieldValidationMessage.value : undefined;
  };

  const updateField = (index: number, value: string) => {
    fields.value[index].value = value;
    isValidationDismissed.value = true;

    if (!duplicateFieldIndices.value.has(index)) return;
    dismissedIndices.value = new Set(dismissedIndices.value).add(index);
  };

  const addField = () => {
    if (canAddField.value) fields.value.push({ id: nextFieldId++, value: '' });
  };

  const removeField = (index: number) => {
    // Removing a field shifts every position after it, so a conflict in flight would land wrong.
    if (props.isSaving) return;

    fields.value.splice(index, 1);
    localDuplicateIndices.value = new Set();
    dismissedIndices.value = new Set(props.duplicateIndices);
  };

  const handleClose = () => emit('close');

  const handleSave = () => {
    if (props.isSaving) return;

    const trimmedFields = fields.value.map((field) => field.value.trim());
    const conflicts = findDuplicateFieldIndices(trimmedFields);

    dismissedIndices.value = new Set();
    localDuplicateIndices.value = conflicts;

    if (conflicts.size > 0) {
      emit('duplicate');
      return;
    }

    isValidationDismissed.value = false;

    // Every field goes out, blanks included, so the positions the API reports back on a conflict
    // line up with the fields on screen. Blank names are discarded on the other side.
    emit('save', trimmedFields);
  };
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-full max-w-md shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <ThemeProvider class="flex flex-col">
      <UiModalHeader
        class="px-6 py-4 text-sm border-b border-gray-02 shrink-0"
        :title="title"
        @close="handleClose"
      />

      <div class="px-6 py-6 flex flex-col gap-3">
        <div
          v-for="(field, index) in fields"
          :key="field.id"
          class="flex items-start gap-2"
        >
          <FormInput
            :id="`settings-field-${field.id}`"
            :model-value="field.value"
            :label="fieldLabel"
            type="text"
            :placeholder="fieldPlaceholder"
            :max-length="NAME_MAX_LENGTH"
            :label-class="index === 0 ? 'text-sm text-gray-07 font-semibold' : 'text-sm text-gray-07 font-semibold invisible'"
            class="flex-1"
            :error="fieldError(index)"
            @update:model-value="updateField(index, $event)"
          />

          <button
            v-if="mode === 'add' && index === 0"
            type="button"
            data-testid="settings-field-add"
            :disabled="!canAddField"
            class="flex size-6 shrink-0 mt-[41px] items-center justify-center text-[var(--theme-secondary-ink)] disabled:opacity-40 disabled:cursor-not-allowed"
            @click="addField"
          >
            <IconImage
              :src="PlusButtonIcon"
              alt="Adicionar campo"
              class="size-6"
            />
          </button>
          <button
            v-else-if="mode === 'add' && index > 0"
            type="button"
            data-testid="settings-field-remove"
            :disabled="isSaving"
            class="flex size-6 shrink-0 mt-[41px] items-center justify-center text-[var(--theme-secondary-ink)] disabled:opacity-40 disabled:cursor-not-allowed"
            @click="removeField(index)"
          >
            <IconImage
              :src="MinusButtonIcon"
              alt="Remover campo"
              class="size-6 -translate-x-0.5 -translate-y-0.5"
            />
          </button>
        </div>

        <p
          v-for="message in batchValidationMessages"
          :key="message"
          data-testid="settings-field-validation-message"
          class="text-red-16 text-sm/5"
        >
          {{ message }}
        </p>
      </div>

      <div class="px-6 py-8 flex justify-end gap-3 border-t border-gray-02 shrink-0">
        <ColoredButton
          text="Cancelar"
          outlined
          color="gray-03"
          :disabled="isSaving"
          @click="handleClose"
        />
        <ColoredButton
          text="Salvar"
          :disabled="!isFormValid || isSaving"
          :loading="isSaving"
          :icon="CheckMarkIcon"
          @click="handleSave"
        />
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>
